import { FONTS, STATUS_COLORS } from '../styles/tokens';
import type { ColorTheme } from '../styles/tokens';
import type { TakContact } from '../api/tak-admin';

interface TakDeviceRowProps {
  contact: TakContact;
  isDark: boolean;
  colors: ColorTheme;
}

function relativeTime(iso?: string): string {
  if (!iso) return 'N/A';
  const diff = Date.now() - new Date(iso).getTime();
  if (diff < 60_000) return 'Just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

function truncateUid(uid: string): string {
  return uid.length > 16 ? uid.slice(0, 14) + '...' : uid;
}

/** One TAK contact row. Stacks into labeled fields on phone widths. */
export function TakDeviceRow({ contact, isDark: _, colors }: TakDeviceRowProps): React.JSX.Element {
  const isOnline = contact.lastStatus?.toLowerCase() === 'connected'
    || contact.lastStatus?.toLowerCase() === 'online';
  const statusColor = isOnline ? STATUS_COLORS.online : STATUS_COLORS.offline;
  const statusLabel = isOnline ? 'Online' : 'Offline';

  const teamColors: Record<string, string> = {
    Cyan: '#00BCD4',
    Blue: '#2196F3',
    Green: '#4CAF50',
    Yellow: '#FFEB3B',
    Red: '#F44336',
    Orange: '#FF9800',
    White: '#FFFFFF',
    Purple: '#9C27B0',
    Magenta: '#E91E63',
    Maroon: '#800000',
    'Dark Green': '#006400',
    Teal: '#008080',
    Brown: '#8B4513',
  };

  const teamColor = contact.team ? teamColors[contact.team] ?? colors.textSecondary : colors.textSecondary;

  return (
    <div
      className="vx-stack-grid"
      style={{
        display: 'grid',
        gridTemplateColumns: '2fr 2fr 1.5fr 1fr 1fr',
        gap: '12px',
        padding: '14px 20px',
        borderBottom: `1px solid ${colors.border}`,
        alignItems: 'center',
        fontFamily: FONTS.body,
        fontSize: '13px',
        color: colors.text,
      }}
    >
      <span className="vx-labeled-cell" data-label="Callsign" style={{ fontWeight: 500 }}>{contact.callsign || 'Unknown'}</span>

      <span
        className="vx-labeled-cell"
        data-label="UID"
        style={{ color: colors.textSecondary, fontSize: '12px', fontFamily: 'monospace' }}
        title={contact.uid}
      >
        {truncateUid(contact.uid)}
      </span>

      <span className="vx-labeled-cell" data-label="Last Seen" style={{ color: colors.textSecondary, fontSize: '12px' }}>
        {relativeTime(contact.lastEventTime)}
      </span>

      <span className="vx-labeled-cell" data-label="Status" style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
        <span
          style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: statusColor,
            display: 'inline-block',
          }}
        />
        <span style={{ fontSize: '12px', color: statusColor }}>{statusLabel}</span>
      </span>

      <span className="vx-labeled-cell" data-label="Team" style={{ color: teamColor, fontSize: '12px', fontWeight: 500 }}>
        {contact.team ?? '—'}
      </span>
    </div>
  );
}
